import { ReactNode, useMemo } from 'react';
import {
  Select,
  TextField,
  DateRangePicker,
  DatepickerSimple,
  AVAILABLE_TIMEZONES,
  Checkbox,
  Box,
  withStyles,
  useForm,
  type FieldData,
  type Timestamp,
  type Timezone,
  type TransactionHistoryStatus,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { Balance } from '#shared/hooks/useApi/useAccountBalances';
import { TransactionTypeLabel } from '#shared/components/UI';
import styles from './Transactions.module.scss';

const cn = withStyles(styles);

// The filter bar's status value drops null: the Select renders '' (the "All" entry) for "no status",
// and onSubmit maps that back to null for the request. 'All' is excluded too — '' is the sole
// no-filter sentinel, so keeping both would render two identical "All" options.
type FilterStatus = Exclude<TransactionHistoryStatus, null | 'All'> | '';

export type TransactionFilterValues = {
  timeRange: [Timestamp, Timestamp];
  timeZone: Timezone;
  currency: string;
  type: string;
  status: FilterStatus;
  flagged: string;
  settlementDate: Timestamp | undefined;
  searchTerm: string;
  includeSubAccounts: boolean;
};

// What the page acts on when the user hits Search — the request-shaped result of the filter form.
export type TransactionSearchParams = {
  type: string;
  status: TransactionHistoryStatus;
  flagged: string;
  currency: string;
  searchTerm: string;
  settlementDate: Timestamp | undefined;
  includeSubAccounts: boolean;
  timeRange: [Timestamp, Timestamp];
  timeZone: Timezone;
};

type UseFilterFormConfigArgs = {
  type: string;
  status: TransactionHistoryStatus;
  flagged: string;
  currency: string;
  searchTerm: string;
  timeZone: Timezone;
  settlementDate: Timestamp | undefined;
  includeSubAccounts: boolean;
  initialTimeRange: [Timestamp, Timestamp];
  balances: Balance[];
  country: string;
  subAccountPermissions: boolean;
  canViewSubaccounts: boolean;
  hasInvalidEndDate: boolean;
  shouldShowEndDateError: boolean;
  setShouldShowEndDateError: (value: boolean) => void;
  onSearch: (searchParams: TransactionSearchParams) => void;
};

// The Transaction History filter bar, driven by the vopay `useForm` config pattern. The host page keeps
// its exact two-row grid, so each field is placed into its own width div via `renderField(name)` (which
// renders the field's input without the default ElementContainer chrome), rather than the stacked
// default renderer. Dropdown vocab (type/status/flagged/currency/timezone) is built here from `t`,
// `balances` and `country`.
export default function useFilterFormConfig(args: UseFilterFormConfigArgs) {
  const {
    type,
    status,
    flagged,
    currency,
    searchTerm,
    timeZone,
    settlementDate,
    includeSubAccounts,
    initialTimeRange,
    balances,
    country,
    subAccountPermissions,
    canViewSubaccounts,
    hasInvalidEndDate,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    onSearch,
  } = args;

  const { t } = useTranslation('translations', { keyPrefix: 'Shared.TransactionHistory.TransactionSearchForm' });

  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const timeZoneOptions = useMemo(() => {
    const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({ label: tz, value: tz })).filter(
      (tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName),
    );

    return [{ label: 'UTC', value: 'UTC' }, { label: timezoneName, value: timezoneName }, ...initialTimeZones];
  }, [timezoneName]);

  const typeOptions: { [key: string]: string } = {
    All: t('TypeOptions.all'),
    'EFT Withdrawal': t('TypeOptions.eftWithdrawal'),
    'EFT Funding': t('TypeOptions.eftFunding'),
    'VoPayInstant Withdraw': t('TypeOptions.voPayInstantWithdraw'),
    Refund: t('TypeOptions.refund'),
    'Currency Conversion': t('TypeOptions.currencyConversion'),
    'CrossBorder Withdrawal': t('TypeOptions.crossBorderWithdrawal'),
    'Interac Money Request': t('TypeOptions.interacMoneyRequest'),
    'Interac Bulk Payout': t('TypeOptions.interacBulkPayout'),
    'Inbound e-Transfer': t('TypeOptions.inboundETransfer'),
    'Visa Direct Push': t('TypeOptions.visaDirectPush'),
    'Credit Card': t('TypeOptions.creditCard'),
    'Google Pay': t('TypeOptions.googlePay'),
    'PayPal Withdrawal': t('TypeOptions.payPalWithdrawal'),
    'Account Transfer': t('TypeOptions.accountTransfer'),
    Reversal: t('TypeOptions.reversal'),
    'Bill Pay': t('TypeOptions.billPay'),
    'Auto Balance Transfer': t('TypeOptions.autoBalanceTransfer'),
    'Virtual Account Deposit': t('TypeOptions.virtualAccountDeposit'),
    'Virtual Account Funding': t('TypeOptions.virtualAccountFunding'),
    'Virtual Account Withdraw': t('TypeOptions.virtualAccountWithdraw'),
    'Virtual Account Wire': t('TypeOptions.virtualAccountWire'),
    'Other Funding': t('TypeOptions.otherFunding'),
    'Other Withdrawal': t('TypeOptions.otherWithdrawal'),
  };

  const typeOptionsUS: { [key: string]: string } = {
    All: t('TypeOptions.all'),
    'ACH Withdrawal': t('TypeOptions.achWithdrawal'),
    'ACH Funding': t('TypeOptions.achFunding'),
    'RTP Withdrawal': t('TypeOptions.rtpWithdrawal'),
    'Fedwire Withdrawal': t('TypeOptions.fedwireWithdrawal'),
    'Fedwire Funding': t('TypeOptions.fedwireFunding'),
    Refund: t('TypeOptions.refund'),
    'Currency Conversion': t('TypeOptions.currencyConversion'),
    'Visa Direct Push': t('TypeOptions.visaDirectPush'),
    'Credit Card': t('TypeOptions.creditCard'),
    'Google Pay': t('TypeOptions.googlePay'),
    'PayPal Withdrawal': t('TypeOptions.payPalWithdrawal'),
    'Venmo Withdrawal': t('TypeOptions.venmoWithdrawal'),
    'Zelle Withdrawal': t('TypeOptions.zelleWithdrawal', { defaultValue: 'Zelle Withdrawal' }),
    'Account Transfer': t('TypeOptions.accountTransfer'),
    Reversal: t('TypeOptions.reversal'),
    'Auto Balance Transfer': t('TypeOptions.autoBalanceTransfer'),
    'Virtual Account Deposit': t('TypeOptions.virtualAccountDeposit'),
    'Virtual Account Funding': t('TypeOptions.virtualAccountFunding'),
    'Virtual Account Withdraw': t('TypeOptions.virtualAccountWithdraw'),
    'Virtual Account Wire': t('TypeOptions.virtualAccountWire'),
    'Other Funding': t('TypeOptions.otherFunding'),
    'Other Withdrawal': t('TypeOptions.otherWithdrawal'),
  };

  const statusOptions: Record<FilterStatus, string> = {
    '': t('StatusOptions.all'),
    requested: t('StatusOptions.requested'),
    pending: t('StatusOptions.pending'),
    'in progress': t('StatusOptions.inProgress'),
    sent: t('StatusOptions.sent'),
    successful: t('StatusOptions.successful'),
    complete: t('StatusOptions.complete'),
    settled: t('StatusOptions.settled'),
    failed: t('StatusOptions.failed'),
    cancelled: t('StatusOptions.cancelled'),
    'cancellation requested': t('StatusOptions.cancellationRequested'),
    'pending approval': t('StatusOptions.pendingApproval'),
    waiting: t('StatusOptions.waiting'),
  };

  const flaggedOptions = {
    All: t('FlaggedOptions.all'),
    Yes: t('FlaggedOptions.yes'),
    No: t('FlaggedOptions.no'),
  };

  const currencyOptions = useMemo(() => {
    const options: { [key: string]: string } = { All: t('CurrencyOptions.all') };

    balances.forEach((balance) => {
      options[balance.Currency] = balance.Currency;
    });

    return options;
  }, [balances]);

  const initialValues: TransactionFilterValues = {
    timeRange: initialTimeRange,
    timeZone,
    currency,
    type,
    status: status === null || status === 'All' ? '' : status,
    flagged,
    settlementDate,
    searchTerm,
    includeSubAccounts,
  };

  const config: FieldData<TransactionFilterValues>[] = [
    {
      fieldName: 'timeRange',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ ctx }) => (
        <DateRangePicker
          onChange={(range) => ctx.setFieldValue('timeRange', range)}
          initialTime={ctx.values.timeRange}
          triggerVariant="outlineShadow"
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={setShouldShowEndDateError}
        />
      ),
    },
    {
      fieldName: 'timeZone',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          placeholder={t('timeZone') || undefined}
          options={timeZoneOptions}
          filterable
          sort={false}
          {...getFormikData<TransactionFilterValues['timeZone']>({})}
        />
      ),
    },
    {
      fieldName: 'currency',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          placeholder={t('currency') || undefined}
          options={convertToOptions(currencyOptions)}
          sort={false}
          {...getFormikData<TransactionFilterValues['currency']>({})}
        />
      ),
    },
    {
      fieldName: 'type',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          placeholder={t('type') || undefined}
          options={country === 'US' ? convertToOptions(typeOptionsUS) : convertToOptions(typeOptions)}
          sort={false}
          // Display only — the option's value (the raw TransactionType) is what gets submitted.
          renderOption={(o, { Option, SelectedIcon, selected }) => (
            <Option>
              <TransactionTypeLabel transactionType={o.value ?? ''} />
              {selected && <SelectedIcon />}
            </Option>
          )}
          // The trigger renders the plain label by default, so it needs the same treatment or the
          // selected rail loses the wordmark the option list showed.
          renderSelectedValue={(_, { value, Placeholder }) =>
            value ? (
              <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                <TransactionTypeLabel transactionType={value} />
              </span>
            ) : (
              <Placeholder />
            )
          }
          {...getFormikData<TransactionFilterValues['type']>({})}
        />
      ),
    },
    {
      fieldName: 'status',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          placeholder={t('status') || undefined}
          options={convertToOptions(statusOptions)}
          sort={false}
          {...getFormikData<TransactionFilterValues['status']>({})}
        />
      ),
    },
    {
      fieldName: 'flagged',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          placeholder={t('flagged') || undefined}
          options={convertToOptions(flaggedOptions)}
          sort={false}
          {...getFormikData<TransactionFilterValues['flagged']>({})}
        />
      ),
    },
    {
      fieldName: 'settlementDate',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ ctx }) => (
        <DatepickerSimple
          variant="outlineShadow"
          value={(ctx.values.settlementDate || '').toString()}
          selected={ctx.values.settlementDate}
          format="timestamp"
          position="bottomLeft"
          onChange={(e) => ctx.setFieldValue('settlementDate', e.target.value ? JSON.parse(e.target.value) : undefined)}
          onBlur={() => {}}
          name="settlementDate"
          placeholder={t('settlementDate', { defaultValue: 'Settlement Date' })}
          inputWidth="auto"
          className={cn('settlementDatePicker')}
          allowBlank
        />
      ),
    },
    {
      fieldName: 'searchTerm',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ ctx }) => (
        <TextField
          key="TransactionSearchKeyword"
          name="TransactionSearchKeyword"
          placeholder={t('keyword') || ''}
          value={ctx.values.searchTerm}
          onChange={(e) => ctx.setFieldValue('searchTerm', e.target.value)}
          onBlur={() => {}}
          variant="outlineShadow"
        />
      ),
    },
    {
      fieldName: 'includeSubAccounts',
      getInputState: () => 'optional',
      showField: () => subAccountPermissions && canViewSubaccounts,
      renderField: ({ ctx }) => (
        <Box width="40%" jc="fs" maxheight="38px" base="8" pb="sm" pt="sm">
          <Checkbox
            label={t('includeSubAccounts') || ''}
            name="IncludeSubAccount"
            variant="border"
            value={ctx.values.includeSubAccounts}
            onChange={(e) => ctx.setFieldValue('includeSubAccounts', e.target.checked)}
            onBlur={() => {}}
            labelSize="16px"
            labelPosition="right"
            hasNoPadding
            disabled={false}
          />
        </Box>
      ),
    },
  ];

  const { ctx, renderFormElements } = useForm<TransactionFilterValues, TransactionFilterValues>({
    initialValues,
    config,
    onSubmit: (values) => {
      onSearch({
        type: values.type,
        status: values.status === '' ? null : values.status,
        flagged: values.flagged,
        currency: values.currency,
        searchTerm: values.searchTerm,
        settlementDate: values.settlementDate,
        includeSubAccounts: values.includeSubAccounts,
        timeRange: values.timeRange,
        timeZone: values.timeZone,
      });
    },
  });

  const byName = useMemo(
    () =>
      Object.fromEntries(config.map((field) => [field.fieldName, field])) as Record<
        keyof TransactionFilterValues,
        FieldData<TransactionFilterValues>
      >,
    [config],
  );

  // Renders a single field's input into its place in the grid, without the default ElementContainer
  // chrome, so the host keeps its exact two-row layout.
  const renderField = (name: keyof TransactionFilterValues): ReactNode =>
    renderFormElements({ config: [byName[name]], override: (templateData) => templateData.template });

  return { ctx, renderField };
}

// The map is keyed by the raw API value (TransactionType, status, currency) with the translated display
// text as its value, so the option takes its label from the value and its submitted value from the key.
function convertToOptions(options: { [key: string]: string }) {
  return Object.entries(options).map(([key, value]) => ({ label: value, value: key }));
}
